import { bigRound } from "c8-numeraljs";
import { evaluate } from "mathjs";
import { C8AttributeId, C8Node, DUMMY, NODE_TYPE } from "./node";
import { C8AttributeType, VALUE_TYPE, cnl } from "./value";

// TODO: move these const values to appropriate place rather than import from csi/reflection,
// as nothing else inside c8-platform depends on csi/reflection, redeclare these constants here
// solution is to could you move the functions that use these constants out of this module (isConversionUOMCustomAttribute too)
const ATTR_VALUE = "Initial Value";
const ATTR_VALUETYPE = "Value Type";

export type UOMConversionId = string;// ex. "LengthUOM"
export type UOMName = string; // ex. "Length"

// UOM related constants
export const CLASS_UNIT_OF_MEASURE = "UnitOfMeasure";
export const CLASS_UOM_CONVERSION = "UOMConversion";
export const CLASS_UOM_MODULE = "UOMModule";
export const CLASS_UOM_TYPE = "UOMType";
export const UOM_SUFFIX = "UOM";
export const URL_UNIT_OF_MEASURE = "centric://UnitOfMeasure/";
export const URL_UOM_CONVERSION = "centric://UOMConversion/";
export const URL_UOM_TYPE = "centric://UOMType/";
export const IS_UOM_CONVERSION_URL = `\%${URL_UOM_CONVERSION}\%`;

export type uomNamePair = {
	/** Name of Unit of Measure */
	uomName?: string;

	/** Name of UOMType */
	uomTypeName?: string;
}

/**
 * Tests if expressions convert correctly back and forth
 * Ex. InternalUOM = ExternalUOM * 12, ExternalUOM = InternalUOM / 12
 * InternalUOM = (InternalUOM / 12) * 12
 * 1 = (1 / 12) * 12
 */
export function areExpressionsInverses(extToIntExpression: string, intToExtExpression: string): boolean {
	if (!extToIntExpression.includes("x") || !intToExtExpression.includes("x")) {
		return false;
	}
	const combinedExpression = extToIntExpression.replace("x", "(" + intToExtExpression + ")");
	const combinedExpressionUsing1 = combinedExpression.replace("x", "1");
	// Round result to 12 decimal places to avoid precision loss from expression evaluation
	return bigRound(evaluate(combinedExpressionUsing1), 12) === 1;
}

/**
 * Convert value to proper Unit of Measure if UOM Attribute
 */
export function convertUOMValue(conversion: C8Node | null, value: number, internalToExternal: boolean): number {
	if (!conversion) {
		return value;
	}

	const expression = internalToExternal ? conversion.IntToExtExpression : conversion.ExtToIntExpression;
	return evaluate(expression.replace("x", value));
}

/**
 * Return the URL for the UnitOfMeasure node
 */
export function generateUnitOfMeasureURL(uomTypeName: string, uomName: UOMName): string {
	if (!uomTypeName || !uomName) {
		return DUMMY;
	}
	return URL_UNIT_OF_MEASURE + uomTypeName + "/" + uomName;
}

/**
 * Return the URL for the UOMConversion node
 */
export function generateUOMConversionURL(uomTypeName: string, uomIntName: UOMName, uomExtName: UOMName): string {
	if (!uomTypeName || !uomIntName || !uomExtName) {
		return DUMMY;
	}
	return URL_UOM_CONVERSION + uomTypeName + "/" + uomIntName + "_" + uomExtName;
}

/**
 * Return the URL for the UOMType node
 */
export function generateUOMTypeURL(uomTypeName: string): string {
	if (!uomTypeName) {
		return DUMMY;
	}
	return URL_UOM_TYPE + uomTypeName;
}

/**
 * Return node attribute type
 * TODO: Will eventually be replaced when reflection model is part of c8-platform
 */
export function getNodeAttributeType(node: C8Node, id: C8AttributeId): C8AttributeType {
	return node?.$attrs?.[id]?.Type;
}

/**
 * Append "UOM" to the end of a string
 */
export function getUOMConversionString(valueString: string): string {
	return valueString ? valueString + UOM_SUFFIX : "";
}

/**
 * Remove "UOM" from the end of a string
 */
export function getUOMValueString(conversionString: string): string {
	return conversionString ? conversionString.slice(0, -UOM_SUFFIX.length) : "";
}

/**
 * Return UOM name
 * TODO: Will eventually be replaced when we use attribute values rather than CNL parsing
 */
export function getUOMNameFromUnitOfMeasureCNL(uomCNL: cnl): string | undefined {
	return uomCNL.split("/").pop();
}

/**
 * Return UOM name and UOMType name
 * TODO: Will eventually be replaced when we use attribute values rather than CNL parsing
 */
export function getUOMNameAndUOMTypeNameFromConversionCNL(conversionCNL: cnl): uomNamePair {
	const arr = conversionCNL.split("/");
	return {
		uomName: conversionCNL.split("_").pop(),
		uomTypeName: arr[arr.length - 2],
	};
}

/**
 * Return true if the conversionNode is a UOM Conversion attribute and valueNode is the corresponding UOM Value attribute
 * Node ends with UOM and has a corresponding attribute pair with the same name (excluding UOM suffix) of type double
 */
export function isConversionUOMAttribute(conversionNode: C8Node, valueNode: C8Node): boolean {
	return conversionNode.$Name.endsWith(UOM_SUFFIX)
		&& isConversionURL(conversionNode[ATTR_VALUE])
		&& valueNode != null && valueNode[ATTR_VALUETYPE] === VALUE_TYPE.DOUBLE;
}

/**
 * Return true if this attribute on node is a UOM conversion attribute and has a corresponding UOM value attribute
 */
export function isConversionUOMAttributeOnNode(node: C8Node, attrName: C8AttributeId): boolean {
	const conversionURL = node[attrName];
	if (typeof conversionURL !== "string" || !isConversionURL(conversionURL)) {
		return false;
	}
	return getNodeAttributeType(node, getUOMValueString(attrName)) === VALUE_TYPE.DOUBLE;
}

/**
 * Return true if the conversionNode is a UOM Conversion custom attribute and valueNode is the corresponding UOM Value custom attribute
 * Node ends with UOM with target class UOMConversion and has a corresponding attribute pair with the same name (excluding UOM suffix) of type double
 */
export function isConversionUOMCustomAttribute(conversionNode: C8Node, valueNode: C8Node): boolean {
	const { $Name, $Type, TargetClassName } = conversionNode;
	return $Name.endsWith(UOM_SUFFIX)
		&& $Type === NODE_TYPE.CUSTOM_ATTRIBUTE
		&& TargetClassName === CLASS_UOM_CONVERSION
		&& valueNode != null && valueNode[ATTR_VALUETYPE] === VALUE_TYPE.DOUBLE;
}

/**
 * Return true if url includes UOMConversion string
 */
export function isConversionURL(url: cnl): boolean {
	return !!url?.includes(URL_UOM_CONVERSION);
}

/**
 * Return true if this attribute on node has a corresponding UOM conversion attribute
 */
export function isValueUOMAttributeOnNode(node: C8Node | null, attrName: C8AttributeId): boolean {
	if (!node) return false;
	const conversionURL = node[getUOMConversionString(attrName)];
	return isConversionURL(conversionURL);
}

/**
 * Return true if the valueNode is a UOM Value attribute and conversionNode is the corresponding UOM Conversion attribute
 * Node is of type double and has corresponding attribute pair with same name and UOM suffix of type UOMConversion
 */
export function isValueUOMCustomAttribute(valueNode: C8Node, conversionNode: C8Node): boolean {
	if (conversionNode == null) {
		return false;
	}
	const { $Type, TargetClassName } = conversionNode;
	return valueNode[ATTR_VALUETYPE] === VALUE_TYPE.DOUBLE
		&& $Type === NODE_TYPE.CUSTOM_ATTRIBUTE
		&& TargetClassName === CLASS_UOM_CONVERSION;
}
